import { useState, useSyncExternalStore } from "react";
import { addPageScrollListener, getPageScrollTop } from "./app-scroll.ts";

export type ScrollDirection = "up" | "down" | "top";

const DIRECTION_CHANGE_DELTA_PX = 8;

const JUMP_DELTA_PX = 400;

export function useScrollDirection(threshold = 80): ScrollDirection {
    const [store] = useState(() => ({
        direction: "top" as ScrollDirection,
        lastY: getPageScrollTop(),
    }));
    return useSyncExternalStore(
        (listener) => {
            let frame: number | null = null;

            function updateDirection() {
                frame = null;

                const y = getPageScrollTop();
                const delta = y - store.lastY;
                let next = store.direction;

                if (y <= threshold) {
                    next = "top";
                    store.lastY = y;
                } else if (
                    store.direction === "top" &&
                    y <= threshold + DIRECTION_CHANGE_DELTA_PX
                ) {
                    store.lastY = y;
                } else if (Math.abs(delta) >= JUMP_DELTA_PX) {
                    next = "up";
                    store.lastY = y;
                } else if (Math.abs(delta) >= DIRECTION_CHANGE_DELTA_PX) {
                    next = delta > 0 ? "down" : "up";
                    store.lastY = y;
                }

                if (next === store.direction) return;

                store.direction = next;
                listener();
            }

            function onScroll() {
                if (frame !== null) return;
                frame = requestAnimationFrame(updateDirection);
            }

            const unsubscribe = addPageScrollListener(onScroll);

            return () => {
                if (frame !== null) window.cancelAnimationFrame(frame);
                unsubscribe();
            };
        },
        () => store.direction,
        () => "top",
    );
}
